"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AccessibilityStatement } from "../../types/statement";
import { Plus } from "lucide-react";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { HelpText } from "../help-text";
interface RelatedEvidenceStepProps {
  data: AccessibilityStatement["relatedEvidence"];
  onChange: (
    data: AccessibilityStatement["relatedEvidence"],
    changedField?: keyof AccessibilityStatement["relatedEvidence"]
  ) => void;
}

export function RelatedEvidenceStep({
  data,
  onChange,
}: RelatedEvidenceStepProps) {
  const handleChange = (
    field: keyof AccessibilityStatement["relatedEvidence"],
    value: string | string[]
  ) => {
    onChange({ ...data, [field]: value }, field);
  };

  const handleOtherEvidenceChange = (index: number, value: string) => {
    const updated = [...data.otherEvidence];
    updated[index] = value;
    onChange({ ...data, otherEvidence: updated }, "otherEvidence");
  };

  const addOtherEvidence = () => {
    onChange(
      { ...data, otherEvidence: [...data.otherEvidence, ""] },
      "otherEvidence"
    );
  };

  return (
    <div className="cy:space-y-6">
      <div>
        <Label className="cy:text-lg cy:font-semibold cy:mb-4! cy:flex! cy:gap-1">
          Related evidence{" "}
          <TooltipWrapper
            className="cy:ml-1"
            content="Add links to any reports, statements, or certifications that support your accessibility claims. This helps build trust and provide clear evidence."
          />
        </Label>
      </div>
      <div className="cy:space-y-4">
        <div>
          <Label
            htmlFor="recentEvaluationReport"
            className="cy:text-base cy:font-semibold"
          >
            Link to recent evaluation report
          </Label>
          <HelpText>
            Example: https://example.org/accessibility-evaluation-report
          </HelpText>
          <Input
            id="recentEvaluationReport"
            type="url"
            value={data.recentEvaluationReport}
            onChange={(e) =>
              handleChange("recentEvaluationReport", e.target.value)
            }
            className="cy:mt-1"
          />
        </div>
        <div>
          <Label
            htmlFor="evaluationStatement"
            className="cy:text-base cy:font-semibold cy:flex! cy:gap-1"
          >
            Link to evaluation statement
            <TooltipWrapper content="An evaluation statement summarises the key findings from an accessibility evaluation report." />
          </Label>
          <HelpText>
            Example: https://example.org/accessibility-evaluation-statement
          </HelpText>
          <Input
            id="evaluationStatement"
            type="url"
            value={data.evaluationStatement}
            onChange={(e) =>
              handleChange("evaluationStatement", e.target.value)
            }
            className="cy:mt-1"
          />
        </div>
        {data.otherEvidence.map((evidence, idx) => (
          <div
            key={idx}
            className="cy:flex cy:items-center cy:gap-2 cy:mt-2"
          >
            <div className="cy:w-full">
              <Label
                htmlFor={`otherEvidence-${idx}`}
                className="cy:text-base cy:font-semibold"
              >
                Other evidence {idx + 1}
              </Label>
              <Input
                id={`otherEvidence-${idx}`}
                type="url"
                value={evidence}
                onChange={(e) => handleOtherEvidenceChange(idx, e.target.value)}
                className="cy:mt-1"
              />
            </div>
            {/* {data.otherEvidence.length > 1 && (
              <button
                type="button"
                onClick={() => removeOtherEvidence(idx)}
                className="cy:text-red-500 cy:text-lg cy:ml-2 cy:mt-6 cy:font-bold cy:rounded cy:bg-transparent cy:border-none cy:cursor-pointer"
                aria-label="Remove evidence"
              >
                &minus;
              </button>
            )} */}
          </div>
        ))}
        <button
          type="button"
          onClick={addOtherEvidence}
          className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
        >
          <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
            <Plus className="cy:w-3 cy:h-3" />
          </span>
          Add another evidence
        </button>
      </div>
    </div>
  );
}
